import { i18n } from 'boot/i18n';
import EditClient from 'components/Clients/Modals/EditClient';
import { chain, find, isEqual, keyBy, omit } from 'lodash';
import { computed } from 'vue';
import { useAuth } from 'src/composables/auth';
import { useClients } from 'src/composables/clients';
import { notifyNegative, useDialog } from 'src/composables/interactions';
import { EDIT_CLIENT_PROFILE_SOURCES } from 'src/constants/analytics';
import UserPrivilegesDB from 'src/fb/userPrivileges-db';
import UsersClientsDB from 'src/fb/usersClients-db';
import { editClientProfileAnalytics } from 'src/services/analytics';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { users as useUsersStore } from 'src/store/users';
import { userRoles } from 'src/utils/AppUtils';

// Shared permission-check and open-flow logic for opening the EditClient modal from
// anywhere in the app (not just Clients.vue) - use openClientProfile/canOpenClientProfile
// instead of re-implementing these checks.
// eslint-disable-next-line import/prefer-default-export
export function useEditClientProfile() {
  const authStore = useAuthStore();
  const clientsStore = useClientsStore();
  const usersStore = useUsersStore();
  const { userHasPrivilege } = useAuth();
  const { openComponentDialog } = useDialog();
  const { getSessionClient } = useClients();

  let cachedClinicUserPrivileges = null;
  let isClientModalOpen = false;

  const canManageUsers = computed(() => (
    authStore.isManagerOrAdministrator || userHasPrivilege('users/manageUsers')
  ));

  const canAddClients = computed(() => userHasPrivilege('clients/addClients'));

  // Based on the /clients router guard.
  const canAccessClientsPage = computed(() => {
    const { role, clinic } = authStore.userDetails || {};
    return Boolean(clinic) && [
      userRoles.MANAGER,
      userRoles.ADMINISTRATOR,
      userRoles.SUPERVISOR,
      userRoles.SENIOR_THERAPIST,
    ].includes(role);
  });

  const hasAccessToClient = (targetClient) => (
    authStore.isManagerOrAdministrator
    || clientsStore.clients?.some(({ id }) => id === targetClient.id)
  ); // hasAccessToClient()

  /**
   * Returns null if the profile can be opened, or an i18n key for the specific denial reason.
   * @param {Object} targetClient
   * @returns {string|null}
   */
  const getOpenClientProfileDenialReason = (targetClient) => {
    if (!userHasPrivilege('pages/clients')) {
      return i18n.t('clients.notifyNoPrivilegeToViewProfile');
    }

    if (!canAccessClientsPage.value) {
      return i18n.t('clients.notifyNoRoleAccessToViewProfile');
    }

    if (!hasAccessToClient(targetClient)) {
      return i18n.t('clients.notifyNoClientAccessToViewProfile');
    }

    return null;
  }; // getOpenClientProfileDenialReason()

  const canOpenClientProfile = (targetClient) => {
    if (!targetClient?.id) {
      return false;
    }

    return getOpenClientProfileDenialReason(targetClient) === null;
  }; // canOpenClientProfile()

  const loadClientAssociatedUsers = async (clientId) => {
    if (!clientId || !canManageUsers.value) {
      return [];
    }

    const usersClientsDb = new UsersClientsDB();
    const associatedClientUsers = await usersClientsDb.readAll([['client', '==', clientId]]);
    await usersStore.bindUsers(authStore.userDetails?.clinic);

    return chain(associatedClientUsers)
      .filter((userClient) => find(usersStore.users, { id: userClient.user }))
      .map((userClient) => userClient.user)
      .value();
  }; // loadClientAssociatedUsers()

  const loadCachedUserPrivileges = async () => {
    const { clinic } = authStore.userDetails || {};
    if (!clinic) {
      return {};
    }

    if (!cachedClinicUserPrivileges) {
      const userPrivilegesDb = new UserPrivilegesDB(clinic);
      const rolePrivileges = await userPrivilegesDb.readAll();
      cachedClinicUserPrivileges = keyBy(rolePrivileges, 'id');
    }

    return cachedClinicUserPrivileges;
  }; // loadCachedUserPrivileges()

  const loadClientDialogData = async (clientId) => {
    try {
      const [associatedUsers, cachedUserPrivileges] = await Promise.all([
        loadClientAssociatedUsers(clientId),
        loadCachedUserPrivileges(),
      ]);

      return { associatedUsers, cachedUserPrivileges };
    } catch (err) {
      notifyNegative(i18n.t('common.errors.databaseReadError'));
      return null;
    }
  }; // loadClientDialogData()

  const normalizeClientForChangeComparison = (rawClient) => ({
    ...omit(rawClient, ['avatarInitials', 'avatarBgColor', 'hasAuthorizations']),
  });

  const openNewClientProfile = async (newClient) => {
    if (!canAddClients.value || isClientModalOpen) {
      return;
    }

    isClientModalOpen = true;

    let cachedUserPrivileges;
    try {
      cachedUserPrivileges = await loadCachedUserPrivileges();
    } catch (err) {
      isClientModalOpen = false;
      notifyNegative(i18n.t('common.errors.databaseReadError'));
      return;
    }

    openComponentDialog(
      EditClient,
      {
        client: newClient,
        associatedUsers: [],
        cachedUserPrivileges,
        tabToOpen: null,
      },
      () => {
        isClientModalOpen = false;
      },
      () => {
        isClientModalOpen = false;
      },
      () => {
        isClientModalOpen = false;
      },
    );
  }; // openNewClientProfile()

  const openClientProfile = async (
    targetClient,
    {
      source = EDIT_CLIENT_PROFILE_SOURCES.UNKNOWN,
      tabToOpen = null,
      onAppointmentsChanged = null,
    } = {},
  ) => {
    if (!targetClient || isClientModalOpen) {
      return;
    }

    const denialReason = getOpenClientProfileDenialReason(targetClient);
    if (denialReason) {
      notifyNegative(denialReason);
      return;
    }

    isClientModalOpen = true;
    const dialogData = await loadClientDialogData(targetClient.id);

    if (!dialogData) {
      isClientModalOpen = false;
      return;
    }

    const { associatedUsers, cachedUserPrivileges } = dialogData;

    editClientProfileAnalytics.trackProfileOpened({ targetClientId: targetClient.id, source });

    openComponentDialog(
      EditClient,
      {
        client: targetClient,
        associatedUsers,
        cachedUserPrivileges,
        tabToOpen,
        source,
        onAppointmentsChanged,
      },
      (updatedClient) => {
        isClientModalOpen = false;

        if (!updatedClient) {
          return;
        }

        const hasChanges = !isEqual(
          normalizeClientForChangeComparison(targetClient),
          normalizeClientForChangeComparison(updatedClient),
        );

        editClientProfileAnalytics.trackProfileSaved({ targetClientId: targetClient.id, source, hasChanges });
      },
      () => {
        isClientModalOpen = false;
        editClientProfileAnalytics.trackProfileClosedWithoutSaving({ targetClientId: targetClient.id, source });
      },
      () => {
        isClientModalOpen = false;
      },
    );
  }; // openClientProfile()

  return {
    canOpenClientProfile,
    canManageUsers,
    canAddClients,
    hasAccessToClient,
    loadClientAssociatedUsers,
    loadCachedUserPrivileges,
    getSessionClient,
    openClientProfile,
    openNewClientProfile,
  };
}
